import React from 'react';
import { Typography } from '@mui/material';
import { formatKey, formatDate } from '@/utils/constants';
import { safelyParseDate } from '@/utils/date';
import { DataGridAuditClientName } from '@/components/table/DataGridComponents/DataGridAuditClientName';
import { DataGridAuditDealName } from '@/components/table/DataGridComponents/DataGridAuditDealName';
import { OwnerCellData } from '../OwnerCell';

const renderAuditUserCell = (user) => {
	const userId = user?.userId;
	const email = user?.email;

	if (!userId && !email) {
		return (
			<Typography variant='body2' color='text.secondary'>
				—
			</Typography>
		);
	}

	return <OwnerCellData userId={userId} email={email} color='text.primary' />;
};

export const formatAuditActionType = (actionType) => {
	if (!actionType) return '';
	return formatKey(actionType);
};

export const formatAuditTimestamp = (value) => {
	const date = safelyParseDate(value);
	if (!date) return '';
	return formatDate(date, true);
};

export const columns = [
	{
		field: 'timestamp',
		headerName: 'Create date',
		flex: 1.2,
		minWidth: 170,
		display: 'flex',
		renderCell: ({ row }) => formatAuditTimestamp(row.timestamp),
	},
	{
		field: 'actionType',
		headerName: 'Activity type',
		flex: 1.1,
		minWidth: 140,
		display: 'flex',
		hideable: false,
		renderCell: ({ row }) => formatAuditActionType(row.actionType),
	},
	{
		field: 'clientName',
		headerName: 'Associated Client',
		flex: 1.2,
		minWidth: 160,
		display: 'flex',
		renderCell: ({ row }) => <DataGridAuditClientName clientId={row.clientId} />,
	},
	{
		field: 'dealName',
		headerName: 'Associated Deal',
		flex: 1.2,
		minWidth: 160,
		display: 'flex',
		renderCell: ({ row }) => <DataGridAuditDealName dealId={row.dealId} />,
	},
	{
		field: 'performedBy',
		headerName: 'User',
		flex: 1.2,
		minWidth: 160,
		display: 'flex',
		renderCell: ({ row }) => renderAuditUserCell(row.performedBy),
	},
	{
		field: 'fieldChanged',
		headerName: 'Field',
		flex: 1,
		minWidth: 120,
		display: 'flex',
		renderCell: ({ row }) => formatKey(row.fieldChanged ?? ''),
	},
	{
		field: 'targetUser',
		headerName: 'Target user',
		flex: 1.2,
		minWidth: 160,
		display: 'flex',
		renderCell: ({ row }) => renderAuditUserCell(row.targetUser),
	},
];

export const csvColumns = [
	{ field: 'timestamp', header: 'Create date', format: formatAuditTimestamp },
	{ field: 'actionType', header: 'Activity type', format: formatAuditActionType },
	{ field: 'clientName', header: 'Associated Client' },
	{ field: 'dealName', header: 'Associated Deal' },
	{
		field: 'performedBy',
		header: 'User',
		format: (value) =>
			typeof value === 'string' ? value : (value?.name ?? value?.email ?? ''),
	},
	{ field: 'fieldChanged', header: 'Field', format: formatAuditActionType },
	{
		field: 'targetUser',
		header: 'Target user',
		format: (value) =>
			typeof value === 'string' ? value : (value?.name ?? value?.email ?? ''),
	},
	{ field: 'previousValue', header: 'Previous value' },
	{ field: 'newValue', header: 'New value' },
];
